/**
 * Self-service password change for the signed-in technician.
 *
 * Hangs off the profile page rather than a settings screen of its own —
 * the profile is already where a tech sees who they are signed in as.
 *
 * Distinct from `/admin/users/{id}/password`, which a manager uses to set
 * someone else's and does NOT re-verify a current one. This route proves
 * you are you before it rewrites anything.
 *
 * Sized and spaced for the shop floor: full-width inputs, big touch
 * targets, and a show/hide toggle, because typing a password twice
 * without seeing it on a greasy tablet is how people give up.
 */
import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { Modal } from '@vollgas/shared/ui'
import { useI18n } from '@/i18n/TranslationContext'
import { ApiError } from '@/apis/apiClient'
import {
  changeOwnPassword,
  PASSWORD_MIN_LENGTH,
  WRONG_CURRENT_PASSWORD_DETAIL,
} from '@/apis/staffApis'

const FIELD =
  'rounded-sm border border-glass-border bg-glass-fill px-3 py-2.5 text-13 font-medium text-glass-text placeholder:text-glass-text-muted focus:border-glass-border-strong focus:outline-none'

interface Props {
  readonly onClose: () => void
}

export function ChangePasswordModal({ onClose }: Props) {
  const { t } = useI18n()

  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [confirm, setConfirm] = useState('')
  const [show, setShow] = useState(false)
  // Field errors stay quiet until a submit is attempted — flagging a
  // too-short password while it is still being typed is just nagging.
  const [touched, setTouched] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState(false)

  const lengthOk = next.length >= PASSWORD_MIN_LENGTH
  const matches = next.length > 0 && next === confirm
  const currentFilled = current.length > 0

  const change = useMutation({
    mutationFn: () => changeOwnPassword({ currentPassword: current, newPassword: next }),
    onSuccess: () => {
      setDone(true)
      setCurrent('')
      setNext('')
      setConfirm('')
    },
    onError: (e: unknown) => {
      // A wrong current password arrives as a 400 whose reason survives
      // only in `detail` — the problem `type` is the generic bad-request,
      // so ApiError.code cannot be matched on.
      if (e instanceof ApiError && e.message === WRONG_CURRENT_PASSWORD_DETAIL) {
        setError(t.profile.errCurrentWrong)
        return
      }
      setError(e instanceof ApiError ? e.message : t.profile.errGeneric)
    },
  })

  function submit(e: React.FormEvent) {
    e.preventDefault()
    setTouched(true)
    if (!currentFilled || !lengthOk || !matches || change.isPending) return
    setError(null)
    change.mutate()
  }

  return (
    <Modal onClose={onClose} closeLabel={t.common.close} size="sm">
      {done ? (
        <div className="flex flex-col gap-4">
          <h2 className="text-15 font-extrabold text-glass-text">{t.profile.changePassword}</h2>
          <p role="status" className="text-13 text-glass-text-muted">
            {t.profile.success}
          </p>
          <div className="flex justify-end">
            <button
              type="button"
              onClick={onClose}
              className="rounded-full bg-accent px-5 py-2.5 text-13 font-bold text-white transition hover:opacity-90"
            >
              {t.common.close}
            </button>
          </div>
        </div>
      ) : (
        <form onSubmit={submit} className="flex flex-col gap-3" noValidate>
          <h2 className="text-15 font-extrabold text-glass-text">{t.profile.changePassword}</h2>

          {error && (
            <p role="alert" className="text-12-5 font-semibold text-accent">
              {error}
            </p>
          )}

          <label className="flex flex-col gap-1 text-13 font-semibold text-glass-text">
            {t.profile.currentPassword}
            <input
              type={show ? 'text' : 'password'}
              autoComplete="current-password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              className={FIELD}
            />
          </label>

          <label className="flex flex-col gap-1 text-13 font-semibold text-glass-text">
            {t.profile.newPassword}
            <input
              type={show ? 'text' : 'password'}
              autoComplete="new-password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              aria-invalid={touched && !lengthOk ? true : undefined}
              className={FIELD}
            />
            {touched && !lengthOk && (
              <span role="alert" className="text-12-5 font-semibold text-accent">
                {t.profile.errLength}
              </span>
            )}
          </label>

          <label className="flex flex-col gap-1 text-13 font-semibold text-glass-text">
            {t.profile.confirmPassword}
            <input
              type={show ? 'text' : 'password'}
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              aria-invalid={touched && !matches ? true : undefined}
              className={FIELD}
            />
            {touched && !matches && (
              <span role="alert" className="text-12-5 font-semibold text-accent">
                {t.profile.errMismatch}
              </span>
            )}
          </label>

          <button
            type="button"
            onClick={() => setShow((v) => !v)}
            className="self-start text-12-5 font-semibold text-glass-text-muted underline"
          >
            {show ? t.profile.hide : t.profile.show}
          </button>

          {/* Says what the change costs. Every other tablet this tech is
              signed in on is signed out — worth knowing BEFORE, not at
              the next bay. */}
          <p className="text-12-5 text-glass-text-muted">{t.profile.sessionHint}</p>

          <div className="mt-1 flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={change.isPending}
              className="rounded-full border border-glass-border bg-transparent px-5 py-2.5 text-13 font-bold text-glass-text transition hover:bg-glass-fill-strong disabled:opacity-50"
            >
              {t.common.cancel}
            </button>
            <button
              type="submit"
              disabled={change.isPending}
              className="rounded-full bg-accent px-5 py-2.5 text-13 font-bold text-white transition hover:opacity-90 disabled:opacity-50"
            >
              {/* "Save", not "Change password" — the heading above
                  already says what this form is, and repeating it gave
                  the trigger and the submit the same accessible name. */}
              {change.isPending ? t.profile.saving : t.common.save}
            </button>
          </div>
        </form>
      )}
    </Modal>
  )
}
